import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Space,
  Tag,
  Typography,
  message,
  Popconfirm,
  Tooltip,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CopyOutlined,
  EyeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import promoCodesService from '../../../services/promoCodes';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const PromoCodesPage = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form] = Form.useForm();
  const [stats, setStats] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchStats();
  }, [pagination.current]);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const data = await promoCodesService.getAll(pagination.current);
      setPromoCodes(data.data);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total
      }));
    } catch (error) {
      console.error('Error fetching promo codes:', error);
      message.error('Error fetching promo codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await promoCodesService.getStats();
      setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const generateCode = async () => {
    setGenerateLoading(true);
    try {
      const data = await promoCodesService.generateCode();
      form.setFieldsValue({ code: data.code });
      message.success('Code generated successfully');
    } catch (error) {
      console.error('Error generating code:', error);
      message.error('Error generating code');
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setFormLoading(true);
    try {
      const payload = {
        ...values,
        valid_from: values.valid_from ? values.valid_from.format('YYYY-MM-DD') : null,
        valid_until: values.valid_until ? values.valid_until.format('YYYY-MM-DD') : null,
        is_active: values.is_active ?? true,
        is_single_use_per_user: values.is_single_use_per_user ?? false
      };

      if (editingCode) {
        await promoCodesService.update(editingCode.id, payload);
        message.success('Promo code updated successfully');
      } else {
        await promoCodesService.create(payload);
        message.success('Promo code created successfully');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingCode(null);
      fetchPromoCodes();
      fetchStats();
    } catch (error) {
      console.error('Error saving promo code:', error);
      message.error('Error saving promo code');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteLoading(id);
    try {
      await promoCodesService.delete(id);
      message.success('Promo code deleted successfully');
      fetchPromoCodes();
      fetchStats();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      message.error('Error deleting promo code');
    } finally {
      setDeleteLoading(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Code copied to clipboard');
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Space>
          <Text code>{code}</Text>
          <Tooltip title="Copy code">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyToClipboard(code)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => {
        let color = 'blue';
        let text = type;
        
        if (type === 'free') {
          color = 'green';
          text = 'Free';
        } else if (type === 'percentage') {
          color = 'orange';
          text = `${record.value}%`;
        } else if (type === 'fixed') {
          color = 'purple';
          text = `$${record.value}`;
        }
        
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => (
        <Text>
          {record.used_count}
          {record.max_uses && ` / ${record.max_uses}`}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Valid Period',
      key: 'valid_period',
      render: (_, record) => {
        const now = dayjs();
        const validFrom = record.valid_from ? dayjs(record.valid_from) : null;
        const validUntil = record.valid_until ? dayjs(record.valid_until) : null;
        
        let status = 'Valid';
        let color = 'green';
        
        if (validFrom && now.isBefore(validFrom)) {
          status = 'Not Started';
          color = 'orange';
        } else if (validUntil && now.isAfter(validUntil)) {
          status = 'Expired';
          color = 'red';
        }
        
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingCode(record);
                form.setFieldsValue({
                  ...record,
                  valid_from: record.valid_from ? dayjs(record.valid_from) : null,
                  valid_until: record.valid_until ? dayjs(record.valid_until) : null
                });
                setModalVisible(true);
              }}
              disabled={deleteLoading === record.id}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this promo code?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            disabled={deleteLoading === record.id}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading === record.id}
                disabled={deleteLoading === record.id}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Promo Codes Management</Title>
        <Text type="secondary">Manage promotional codes for lead purchases</Text>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Promo Codes"
              value={stats.total || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Codes"
              value={stats.active || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive Codes"
              value={stats.inactive || 0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expired Codes"
              value={stats.expired || 0}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCode(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Add Promo Code
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchPromoCodes();
                fetchStats();
              }}
            >
              Refresh
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={promoCodes}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page) => setPagination(prev => ({ ...prev, current: page }))
          }}
        />
      </Card>

      <Modal
        title={editingCode ? 'Edit Promo Code' : 'Add Promo Code'}
        open={modalVisible}
        onCancel={() => {
          if (!formLoading) {
            setModalVisible(false);
            setEditingCode(null);
            form.resetFields();
          }
        }}
        footer={null}
        width={600}
        maskClosable={!formLoading}
        closable={!formLoading}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            type: 'free',
            is_active: true,
            is_single_use_per_user: false
          }}
        >
          <Form.Item
            name="code"
            label="Promo Code"
            rules={[{ required: true, message: 'Please enter a promo code' }]}
          >
            <Input
              placeholder="Enter promo code"
              addonAfter={
                <Button 
                  type="text" 
                  onClick={generateCode}
                  loading={generateLoading}
                  disabled={formLoading}
                >
                  Generate
                </Button>
              }
              disabled={formLoading}
            />
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Enter promo code name" disabled={formLoading} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter description (optional)" disabled={formLoading} />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select placeholder="Select type" disabled={formLoading}>
              <Option value="free">Free</Option>
              <Option value="percentage">Percentage Discount</Option>
              <Option value="fixed">Fixed Amount Discount</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              if (type === 'percentage' || type === 'fixed') {
                return (
                  <Form.Item
                    name="value"
                    label={type === 'percentage' ? 'Percentage' : 'Amount'}
                    rules={[{ required: true, message: `Please enter ${type === 'percentage' ? 'percentage' : 'amount'}` }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      max={type === 'percentage' ? 100 : undefined}
                      placeholder={type === 'percentage' ? 'Enter percentage (0-100)' : 'Enter amount'}
                      addonAfter={type === 'percentage' ? '%' : '$'}
                      disabled={formLoading}
                    />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="max_uses"
            label="Maximum Uses"
          >
            <Input
              type="number"
              min={1}
              placeholder="Leave empty for unlimited uses"
              disabled={formLoading}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="valid_from"
                label="Valid From"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select start date"
                  disabled={formLoading}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="valid_until"
                label="Valid Until"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select end date"
                  disabled={formLoading}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="Active"
            valuePropName="checked"
          >
            <Switch disabled={formLoading} />
          </Form.Item>

          <Form.Item
            name="is_single_use_per_user"
            label="Single Use Per User"
            valuePropName="checked"
          >
            <Switch disabled={formLoading} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={formLoading}
                disabled={formLoading}
              >
                {editingCode ? 'Update' : 'Create'}
              </Button>
              <Button 
                onClick={() => {
                  if (!formLoading) {
                    setModalVisible(false);
                    setEditingCode(null);
                    form.resetFields();
                  }
                }}
                disabled={formLoading}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromoCodesPage; 